import React, {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from "react";
import { COUPONS } from "./shared";
import { useAuth } from "./auth";
import { trackAddToCart, trackRemoveFromCart } from "./analytics";

/**********************
 * Cart Context (unchanged API, adds metal to key)
 *********************/
const CartContext = createContext(null);

function cartReducer(state, action) {
  switch (action.type) {
    case "HYDRATE": {
      return { ...state, items: action.items || {} };
    }
    case "REPLACE": {
      return action.state;
    }
    case "ADD": {
      const {
        id,
        name,
        image,
        size,
        metal,
        stoneType,
        insuranceCost,
        unitPrice,
        qty,
      } = action.payload;
      const key = `${id}@${metal || "na"}@${size ?? "na"}@${stoneType || "na"}`;
      const existing = state.items[key];
      const nextQty = (existing?.qty ?? 0) + qty;
      const nextItems = {
        ...state.items,
        [key]: {
          id,
          name,
          image,
          size,
          metal,
          stoneType,
          insuranceCost,
          unitPrice,
          qty: nextQty,
        },
      };
      return { ...state, items: nextItems };
    }
    case "REMOVE": {
      const next = { ...state.items };
      delete next[action.key];
      return { ...state, items: next };
    }
    case "SET_QTY": {
      const { key, qty } = action;
      if (qty <= 0) {
        const next = { ...state.items };
        delete next[key];
        return { ...state, items: next };
      }
      return {
        ...state,
        items: { ...state.items, [key]: { ...state.items[key], qty } },
      };
    }
    case "CLEAR":
      return { ...state, items: {} };
    case "APPLY_COUPON":
      return { ...state, coupon: action.coupon };
    case "REMOVE_COUPON":
      return { ...state, coupon: null };
    case "SET_SHIPPING":
      return { ...state, shipping: { ...state.shipping, ...action.shipping } };
    default:
      return state;
  }
}

export function useCart() {
  return useContext(CartContext);
}

export function CartProvider({ children }) {
  const CART_GUEST_KEY = "cart:guest:v1";
  const cartUserKey = (userId) => `cart:user:${userId}:v1`;

  const [state, dispatch] = useReducer(cartReducer, undefined, () => {
    try {
      const raw = localStorage.getItem(CART_GUEST_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && typeof parsed === "object") {
        if (parsed.coupon === undefined) parsed.coupon = null;
        if (!parsed.shipping || typeof parsed.shipping !== "object") {
          parsed.shipping = {
            method: "basic",
            zip: "",
            state: "TX",
            country: "US",
          };
        }
        if (parsed.shipping?.method === "standard")
          parsed.shipping.method = "basic";
        return parsed;
      }

      return {
        items: {},
        coupon: null,
        shipping: {
          method: "basic",
          zip: "",
          state: "TX",
          country: "US",
        },
      };
    } catch {
      return {
        items: {},
        coupon: null,
        shipping: { method: "basic", zip: "", state: "TX", country: "US" },
      };
    }
  });

  const { user, authFetch } = useAuth();
  const hydratedUserIdRef = useRef(null);
  const activeCartOwnerRef = useRef("guest");
  const prevUserIdRef = useRef(user?.id || null);
  const pendingGuestMergeItemsRef = useRef(null);

  const loadLocalCartState = (key) => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return null;
      if (!parsed.items || typeof parsed.items !== "object") parsed.items = {};
      if (parsed.coupon === undefined) parsed.coupon = null;
      if (!parsed.shipping || typeof parsed.shipping !== "object") {
        parsed.shipping = {
          method: "standard",
          zip: "",
          state: "TX",
          country: "US",
        };
      }
      return parsed;
    } catch {
      return null;
    }
  };

  const defaultCartState = () => ({
    items: {},
    coupon: null,
    shipping: { method: "basic", zip: "", state: "TX", country: "US" },
  });

  useEffect(() => {
    const prevUserId = prevUserIdRef.current;
    const nextUserId = user?.id || null;
    if (prevUserId === nextUserId) return;

    // Transition: logged-in -> logged-out
    if (prevUserId && !nextUserId) {
      try {
        localStorage.setItem(cartUserKey(prevUserId), JSON.stringify(state));
      } catch {}

      hydratedUserIdRef.current = null;
      activeCartOwnerRef.current = "guest";
      const guestState =
        loadLocalCartState(CART_GUEST_KEY) || defaultCartState();
      dispatch({ type: "REPLACE", state: guestState });
    }

    // Transition: logged-out -> logged-in
    if (!prevUserId && nextUserId) {
      try {
        localStorage.setItem(CART_GUEST_KEY, JSON.stringify(state));
      } catch {}

      const guestItems = state.items || {};
      pendingGuestMergeItemsRef.current = Object.keys(guestItems).length
        ? { ...guestItems }
        : null;

      hydratedUserIdRef.current = null;
      activeCartOwnerRef.current = nextUserId;
      const localUserState =
        loadLocalCartState(cartUserKey(nextUserId)) || defaultCartState();
      dispatch({ type: "REPLACE", state: localUserState });
    }

    prevUserIdRef.current = nextUserId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    let mounted = true;
    if (!user?.id) return;
    if (hydratedUserIdRef.current === user.id) return;

    (async () => {
      try {
        const data = await authFetch("/users/cart");
        if (!mounted) return;
        const itemsArr = Array.isArray(data?.items) ? data.items : [];
        const itemsObj = {};
        for (const it of itemsArr) {
          if (!it) continue;
          const key =
            it.key ||
            `${it.id || it.productId}@${it.metal || "na"}@${it.size ?? "na"}@${it.stoneType || "na"}`;
          const id = it.id || it.productId;
          if (!key || !id) continue;
          itemsObj[key] = {
            id,
            name: it.name,
            image: it.image,
            size: it.size,
            metal: it.metal,
            stoneType: it.stoneType,
            insuranceCost: it.insuranceCost,
            unitPrice: it.unitPrice,
            qty: it.qty,
          };
        }

        const guestItems = pendingGuestMergeItemsRef.current;
        if (guestItems && typeof guestItems === "object") {
          for (const [k, v] of Object.entries(guestItems)) {
            if (!v) continue;
            const existing = itemsObj[k];
            if (existing) {
              const a = Number(existing.qty) || 0;
              const b = Number(v.qty) || 0;
              itemsObj[k] = { ...existing, qty: a + b };
            } else {
              itemsObj[k] = v;
            }
          }

          try {
            localStorage.setItem(
              CART_GUEST_KEY,
              JSON.stringify(defaultCartState()),
            );
          } catch {}
        }
        pendingGuestMergeItemsRef.current = null;

        hydratedUserIdRef.current = user.id;
        dispatch({ type: "HYDRATE", items: itemsObj });
      } catch {
        // leave hydratedUserIdRef unset so we can retry on the next render
      }
    })();

    return () => {
      mounted = false;
    };
  }, [user?.id, authFetch]);

  useEffect(() => {
    try {
      const owner = user?.id ? cartUserKey(user.id) : CART_GUEST_KEY;
      localStorage.setItem(owner, JSON.stringify(state));
    } catch {}
  }, [state, user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    if (hydratedUserIdRef.current !== user.id) return;

    const t = setTimeout(() => {
      const items = Object.entries(state.items || {}).map(([key, it]) => ({
        key,
        productId: it.id,
        id: it.id,
        name: it.name,
        image: it.image,
        size: it.size,
        metal: it.metal,
        stoneType: it.stoneType,
        insuranceCost: it.insuranceCost,
        unitPrice: it.unitPrice,
        qty: it.qty,
      }));
      authFetch("/users/cart", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      }).catch(() => {});
    }, 300);

    return () => clearTimeout(t);
  }, [state.items, user?.id, authFetch]);

  const addToCart = (item) => {
    dispatch({ type: "ADD", payload: item });
    try {
      const GA4_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID || "";
      if (GA4_ID) {
        trackAddToCart({
          measurementId: GA4_ID,
          sku: String(item?.id || item?.sku || ""),
          name: String(item?.name || ""),
          price: Number(item?.unitPrice) || 0,
          quantity: Number(item?.qty) || 1,
        });
      }
    } catch {}
  };

  const removeFromCart = (key) => {
    const it = state.items?.[key];
    dispatch({ type: "REMOVE", key });
    try {
      const GA4_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID || "";
      if (GA4_ID && it) {
        trackRemoveFromCart({
          measurementId: GA4_ID,
          sku: String(it?.id || it?.sku || ""),
          name: String(it?.name || ""),
          price: Number(it?.unitPrice) || 0,
          quantity: Number(it?.qty) || 1,
        });
      }
    } catch {}
  };

  const setQty = (key, qty) => {
    const it = state.items?.[key];
    const prevQty = Number(it?.qty) || 0;
    const nextQty = Number(qty) || 0;
    dispatch({ type: "SET_QTY", key, qty: nextQty });

    if (!it) return;
    const delta = nextQty - prevQty;
    if (!delta) return;

    try {
      const GA4_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID || "";
      if (!GA4_ID) return;
      if (delta > 0) {
        trackAddToCart({
          measurementId: GA4_ID,
          sku: String(it?.id || it?.sku || ""),
          name: String(it?.name || ""),
          price: Number(it?.unitPrice) || 0,
          quantity: delta,
        });
      } else {
        trackRemoveFromCart({
          measurementId: GA4_ID,
          sku: String(it?.id || it?.sku || ""),
          name: String(it?.name || ""),
          price: Number(it?.unitPrice) || 0,
          quantity: Math.abs(delta),
        });
      }
    } catch {}
  };
  const clearCart = () => {
    dispatch({ type: "CLEAR" });

    try {
      const ownerKey = user?.id ? cartUserKey(user.id) : CART_GUEST_KEY;
      localStorage.setItem(ownerKey, JSON.stringify(defaultCartState()));
    } catch {}

    if (user?.id && hydratedUserIdRef.current === user.id) {
      authFetch("/users/cart/clear", { method: "POST" }).catch(() => {});
    }
  };

  const applyCoupon = (rawCode) => {
    const code = String(rawCode || "")
      .toUpperCase()
      .trim();
    const coupon = COUPONS[code];
    if (!code) return { ok: false, message: "Enter a code" };
    if (!coupon) return { ok: false, message: "Invalid code" };
    dispatch({ type: "APPLY_COUPON", coupon });
    return { ok: true, message: `${coupon.label} applied` };
  };

  const removeCoupon = () => dispatch({ type: "REMOVE_COUPON" });
  const setShipping = (shipping) =>
    dispatch({ type: "SET_SHIPPING", shipping });

  const value = {
    state,
    addToCart,
    removeFromCart,
    setQty,
    clearCart,
    applyCoupon,
    removeCoupon,
    setShipping,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
